import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Contact as ContactIcon, Upload } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { BarraDeClientes } from '@/components/cartera/BarraDeClientes';
import { columnasDeClientes } from '@/components/cartera/columnas-de-clientes';
import { TablaDeClientes } from '@/components/cartera/TablaDeClientes';
import { TableroPorEtapa } from '@/components/cartera/TableroPorEtapa';
import { TarjetaDeProgreso } from '@/components/cartera/TarjetaDeProgreso';
import { SaludDeFuentes } from '@/components/cartera/SaludDeFuentes';
import { VentanaDeFicha } from '@/components/cartera/VentanaDeFicha';
import { csvDe, descargarCsv } from '@/components/datos/tabla-pura';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Spinner } from '@/components/ui/Spinner';
import { useAbrirChat } from '@/hooks/useAbrirChat';
import { useFiltrosEnUrl } from '@/hooks/useFiltrosEnUrl';
import { textoDeResultados } from '@/lib/superfiltro';
import { useCrmView } from '@/hooks/useCrmView';
import { useEdicionDeClientes } from '@/hooks/useEdicionDeClientes';
import { useMenu } from '@/hooks/useMenu';
import { usePreferencia } from '@/hooks/usePreferencia';
import { useToast } from '@/hooks/useToast';
import { listarCartera, listarFuentes, type FilaDeCartera } from '@/api/cartera.api';
import { PIPELINE_COLUMNS } from '@/lib/crm';
import { formatPhoneNumber } from '@/lib/format';
import { cn } from '@/lib/cn';
import { leadStatusOptionsFrom } from '@/lib/lead-fields';
import { alAbrir, prefDeArranque, sanearPrefCartera, type PrefCartera } from '@/lib/pref-cartera';
import { almacenDelNavegador } from '@/lib/preferencias';
import { alSoltar, columnasALaVista } from '@/lib/tablero-clientes';
import { FILTROS, cumpleLegible, diasHastaCumple, fechaLegible, filtrar, fuentesDe, legible, ordenar, porLetra, type Filtro, type Orden } from '@/lib/cartera';

/**
 * CLIENTES (tanda 5, Ola A; reunión con el dueño del 5-oct).
 *
 * Toda su cartera A–Z: los de los chats, los de su Excel y los de los anuncios. La
 * busca, la filtra (cartera, prospectos, compra futura, etapa…), la ordena (apellido,
 * cumpleaños, fecha de entrada) y la ve como él prefiera.
 *
 * Tanda 6: cuatro vistas de la misma lista (la tabla en grupos que se edita en la celda,
 * el kanban que se arrastra, tarjetas con su línea de progreso y lista A–Z) bajo una sola barra
 * de herramientas (`BarraDeClientes`). Cómo la deja armada cada quien (vista, filtro, orden,
 * columnas…) lo sigue de la laptop al teléfono (preferencia `cartera`) y se puede guardar con un
 * nombre en «Mis vistas». En todas, abrir un cliente abre la ventana de su ficha, con «Abrir el
 * chat» y «Asignar» al pie: ya no hay barra fija abajo ni panel lateral en esta pantalla.
 */

const LETRAS = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];
const temperaturaDe = (f: FilaDeCartera) => PIPELINE_COLUMNS.find((c) => c.status === f.conversacion?.temperatura);

function PuntoDeTemperatura({ fila }: { fila: FilaDeCartera }) {
  const t = temperaturaDe(fila);
  const etiqueta = t?.label ?? 'Sin temperatura';
  return (
    <span title={etiqueta} className={cn('h-2.5 w-2.5 shrink-0 rounded-full', t?.dot ?? 'bg-slate-300 dark:bg-slate-600')}>
      <span className="sr-only">{etiqueta}</span>
    </span>
  );
}

/** Una fila de la lista. En el celular se apila. Sin estado: se prueba con renderToStaticMarkup. */
export function FilaCliente({ fila, hoy, elegida = false, onAbrir }: { fila: FilaDeCartera; hoy: Date; elegida?: boolean; onAbrir?: () => void }) {
  const cumple = cumpleLegible(fila.cumpleanos);
  const dias = diasHastaCumple(fila.cumpleanos, hoy);
  return (
    <button
      type="button"
      onClick={onAbrir}
      className={cn(
        'flex min-h-[44px] w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-slate-50 sm:flex-row sm:items-center sm:gap-4 dark:hover:bg-slate-800/50',
        elegida && 'bg-brand-50 dark:bg-brand-900/20',
      )}
    >
      <span className="flex min-w-0 items-center gap-2 sm:w-60 sm:shrink-0">
        <PuntoDeTemperatura fila={fila} />
        <span className="truncate font-medium text-slate-900 dark:text-slate-100">{fila.nombre}</span>
      </span>
      <span className="text-sm text-slate-500 sm:w-40 sm:shrink-0 dark:text-slate-400">{formatPhoneNumber(fila.telefono) || '—'}</span>
      <span className="flex flex-wrap items-center gap-1.5 text-xs">
        {fila.personal && <Badge>Personal</Badge>}
        {fila.etapa && <Badge variant="brand">{legible(fila.etapa)}</Badge>}
        {fila.tipo && <Badge>{legible(fila.tipo)}</Badge>}
        {cumple && <span className="text-slate-500 dark:text-slate-400">{`Cumple ${cumple}`}</span>}
        {dias !== null && dias <= 7 && (
          <Badge variant="warning">{dias === 0 ? '🎂 hoy' : `🎂 en ${dias} ${dias === 1 ? 'día' : 'días'}`}</Badge>
        )}
      </span>
      <span className="text-xs text-slate-400 sm:ml-auto sm:shrink-0">{`entró el ${fechaLegible(fila.entroEl)}`}</span>
    </button>
  );
}

function Filas({ filas, hoy, elegidoId, onAbrir }: { filas: FilaDeCartera[]; hoy: Date; elegidoId?: string | null; onAbrir: (f: FilaDeCartera) => void }) {
  return (
    <Card className="overflow-hidden">
      <ul className="divide-y divide-surface-border dark:divide-slate-800">
        {filas.map((f) => (
          <li key={f.contactId}>
            <FilaCliente fila={f} hoy={hoy} elegida={f.contactId === elegidoId} onAbrir={() => onAbrir(f)} />
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * La lista. Por nombre o por apellido va agrupada por letra, con los encabezados
 * pegajosos y la barra A–Z; por cumpleaños o por fecha, la letra no dice nada y va seguida.
 */
export function ListaDeClientes({
  filas,
  hoy,
  orden,
  elegidoId,
  onAbrir,
}: {
  filas: FilaDeCartera[];
  hoy: Date;
  orden: Orden;
  elegidoId?: string | null;
  onAbrir: (f: FilaDeCartera) => void;
}) {
  if (orden !== 'nombre' && orden !== 'apellido') return <Filas filas={filas} hoy={hoy} elegidoId={elegidoId} onAbrir={onAbrir} />;
  const grupos = porLetra(filas, orden === 'apellido');
  const conContactos = new Set(grupos.map((g) => g.letra));
  return (
    <div className="flex gap-1">
      <div className="min-w-0 flex-1 space-y-3">
        {grupos.map((g) => (
          <section key={g.letra} id={`letra-${g.letra}`} aria-label={`Letra ${g.letra}`}>
            <h2 className="sticky top-0 z-10 bg-surface-muted py-1 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:bg-slate-950 dark:text-slate-400">
              {g.letra}
            </h2>
            <Filas filas={g.filas} hoy={hoy} elegidoId={elegidoId} onAbrir={onAbrir} />
          </section>
        ))}
      </div>
      <nav aria-label="Saltar a una letra" className="sticky top-2 flex h-fit flex-col self-start">
        {LETRAS.map((l) => (
          <button
            key={l}
            type="button"
            disabled={!conContactos.has(l)}
            onClick={() => document.getElementById(`letra-${l}`)?.scrollIntoView({ block: 'start' })}
            className="px-1.5 text-[11px] font-semibold leading-[1.15rem] text-brand-600 disabled:text-slate-300 dark:text-brand-300 dark:disabled:text-slate-700"
          >
            {l}
          </button>
        ))}
      </nav>
    </div>
  );
}

export function SinContactos({ onImportar }: { onImportar?: () => void }) {
  return (
    <EmptyState
      icon={ContactIcon}
      title="Todavía no hay contactos."
      description="Importa tu Excel o espera el primer chat."
      action={
        onImportar && (
          <Button variant="outline" onClick={onImportar}>
            <Upload className="h-4 w-4" /> Importar contactos
          </Button>
        )
      }
    />
  );
}

const ALTO = 'min-h-[44px] md:min-h-0';
const TANDA_DE_TARJETAS = 60;
/** El mismo corte (`md`) con que el panel saca la barra lateral. */
const esEscritorio = () => typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

/**
 * La vista «Tarjetas»: una `TarjetaDeProgreso` por cliente, en el orden que traen las filas.
 * `chatDe` dice qué hace «Chat» con cada uno (o `undefined`: sin chat ni teléfono no hay botón).
 */
export function RejillaDeTarjetas({
  filas,
  etapas,
  ahora,
  onVerFicha,
  chatDe,
}: {
  filas: FilaDeCartera[];
  etapas: readonly string[];
  ahora: Date;
  onVerFicha: (f: FilaDeCartera) => void;
  chatDe: (f: FilaDeCartera) => (() => void) | undefined;
}) {
  // ponytail: sin virtualización; entra si la lista pasa de unos cientos.
  const [cuantas, setCuantas] = useState(TANDA_DE_TARJETAS);
  const quedan = filas.length - cuantas;
  return (
    <div className="space-y-5">
      <ul className="grid grid-cols-1 gap-5 lg:grid-cols-2 2xl:grid-cols-3">
        {filas.slice(0, cuantas).map((f) => (
          <li key={f.contactId} className="min-w-0">
            <TarjetaDeProgreso fila={f} etapas={etapas} ahora={ahora} onVerFicha={() => onVerFicha(f)} onAbrirChat={chatDe(f)} />
          </li>
        ))}
      </ul>
      {quedan > 0 && (
        <div className="flex justify-center">
          <Button variant="outline" className={ALTO} onClick={() => setCuantas((c) => c + TANDA_DE_TARJETAS)}>
            Ver más
            <span className="text-slate-400">{quedan === 1 ? '(queda 1)' : `(quedan ${quedan})`}</span>
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * T14: la vista «Fuentes»: la salud de cada base de clientes, calculada en el servidor. Tocar una
 * vuelve a la lista (la tabla en escritorio, las tarjetas en el teléfono) con esa fuente filtrada.
 */
function VistaDeFuentes({ onElegir }: { onElegir: (fuente: string) => void }) {
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['cartera', 'fuentes'], queryFn: listarFuentes });
  if (isLoading)
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  if (isError || !data)
    return (
      <Card>
        <CardBody className="space-y-3 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">No se pudo medir tus fuentes.</p>
          <Button variant="outline" className={ALTO} onClick={() => void refetch()}>
            Reintentar
          </Button>
        </CardBody>
      </Card>
    );
  return <SaludDeFuentes fuentes={data} onElegir={onElegir} />;
}

export default function Cartera() {
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ['cartera'], queryFn: listarCartera });
  // «Importar» solo si el negocio tiene el módulo: el servidor dice qué rutas existen.
  const { datos: menu } = useMenu();
  const puedeImportar = menu?.rutas.some((r) => r.ruta === '/contacts/importar') ?? false;
  const importar = puedeImportar ? () => navigate('/contacts/importar') : undefined;

  // La búsqueda no se guarda con la preferencia ni en las vistas: va en la URL (sobrevive al recargar).
  const { filtros: enUrl, ponerVarios } = useFiltrosEnUrl({ buscar: '' });
  const busqueda = enUrl.buscar;
  const setBusqueda = (buscar: string) => ponerVarios({ buscar });
  // Quien eligió «Lista» o «Tablero» con la pantalla vieja arranca ahí; los demás, con la tabla
  // en escritorio y las tarjetas en el teléfono, hasta que elijan.
  const [arranque] = useState(() => prefDeArranque(almacenDelNavegador(), esEscritorio()));
  const [guardada, setGuardada] = usePreferencia('cartera', arranque, sanearPrefCartera);
  // La pantalla ABRE con la vista «de entrada», si hay una marcada; si no, como la dejaron. No se
  // aplica con un efecto: mientras no se toque nada, lo que se ve se calcula de lo guardado (que
  // llega primero del navegador y después del servidor), y con el primer cambio queda escrito.
  const [tocada, setTocada] = useState(false);
  const yaTocada = useRef(false);
  const pref = useMemo(() => (tocada ? guardada : alAbrir(guardada)), [tocada, guardada]);
  const cambiarPref = useCallback(
    (cambio: (antes: PrefCartera) => PrefCartera) => {
      const primerCambio = !yaTocada.current;
      yaTocada.current = true;
      setTocada(true);
      setGuardada((antes) => cambio(primerCambio ? alAbrir(antes) : antes));
    },
    [setGuardada],
  );
  const { vista, orden, filtro } = pref;

  const [elegidoId, setElegidoId] = useState<string | null>(null);
  const [hoy] = useState(() => new Date());
  const chatDe = useAbrirChat();
  // Los campos de la ficha del negocio son columnas de la tabla, y `edicion` guarda lo que se
  // cambia en una celda o al mover una tarjeta del kanban.
  const crm = useCrmView()?.crm;
  const edicion = useEdicionDeClientes();

  const filas = useMemo(() => data?.filas ?? [], [data]);
  const etapas = useMemo(() => data?.etapas ?? [], [data]);
  // Una etapa guardada que el negocio ya no tiene no deja la lista vacía: es como no filtrar por etapa.
  const etapa = etapas.includes(pref.etapa) ? pref.etapa : '';
  // La búsqueda y la etapa, una vez; encima, cada filtro cuenta los suyos.
  const base = useMemo(() => filtrar(filas, { filtro: 'todos', etapa, etapas, fuentes: pref.fuentes, busqueda }), [filas, etapa, etapas, pref.fuentes, busqueda]);
  // Las fuentes salen de toda la cartera, no de lo filtrado: así un chip no desaparece al tocarlo.
  const listaDeFuentes = useMemo(() => fuentesDe(filas), [filas]);
  const nombresDeFuentes = useMemo(() => listaDeFuentes.map((x) => x.fuente), [listaDeFuentes]);
  const verFuente = useCallback((fuente: string) => cambiarPref((antes) => ({ ...antes, vista: esEscritorio() ? 'tabla' : 'tarjetas', fuentes: [fuente] })), [cambiarPref]);
  const cuentas = useMemo(() => Object.fromEntries(FILTROS.map((f) => [f.id, base.filter(f.cumple).length])) as Record<Filtro, number>, [base]);
  const visibles = useMemo(() => ordenar(filtrar(base, { filtro }), orden, hoy), [base, filtro, orden, hoy]);
  const elegido = filas.find((f) => f.contactId === elegidoId) ?? null;

  const abrir = useCallback((f: FilaDeCartera) => setElegidoId(f.contactId), []);
  const cerrarFicha = useCallback(() => setElegidoId(null), []);

  const campos = crm?.editableFields ?? [];
  const temperaturas = leadStatusOptionsFrom(crm);
  // Las mismas columnas de la tabla: la barra lista sus títulos y «Exportar» saca sus valores.
  const columnas = columnasDeClientes({ campos, etapas, temperaturas, ahora: hoy, edicion, onVerFicha: abrir, guardadas: pref.columnas });
  const exportar = () => {
    const aLaVista = columnasALaVista(pref.columnas);
    descargarCsv('clientes', csvDe(visibles, columnas.filter((c) => aLaVista.includes(c.id))));
  };

  /** El kanban: soltar una tarjeta en otra columna, o elegirla en su «Mover a…». */
  const mover = (fila: FilaDeCartera, destino: string) => {
    const que = alSoltar(fila, destino, etapas);
    if (que.tipo === 'no') toast.warning(que.porque);
    // `cambiarEtapa` pregunta antes de cerrar, pinta el cambio en el acto y vuelve atrás si el servidor no quiere.
    else if (que.tipo !== 'nada') edicion.cambiarEtapa(fila, que.etapa);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Clientes"
        description={
          data ? `${filas.length} ${filas.length === 1 ? 'contacto' : 'contactos'}. Toca uno para ver su ficha, abrir el chat o asignarlo.` : undefined
        }
      />

      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : isError || !data ? (
        <Card>
          <CardBody className="space-y-3 text-center">
            <p className="text-sm text-slate-500 dark:text-slate-400">No se pudo cargar tu cartera.</p>
            <Button variant="outline" className={ALTO} onClick={() => void refetch()}>
              Reintentar
            </Button>
          </CardBody>
        </Card>
      ) : filas.length === 0 ? (
        <SinContactos onImportar={importar} />
      ) : (
        <>
          <BarraDeClientes
            pref={pref}
            onPref={cambiarPref}
            busqueda={busqueda}
            onBuscar={setBusqueda}
            etapas={etapas}
            etapa={etapa}
            cuentas={cuentas}
            fuentes={listaDeFuentes}
            resultados={textoDeResultados(visibles.length, filas.length, ['contacto', 'contactos'])}
            columnas={columnas}
            onImportar={importar}
            onExportar={visibles.length > 0 ? exportar : undefined}
          />

          {vista === 'fuentes' ? (
            <VistaDeFuentes onElegir={verFuente} />
          ) : visibles.length === 0 ? (
            <p className="rounded-lg border border-dashed border-surface-border px-4 py-5 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
              Nadie coincide con la búsqueda o el filtro.
            </p>
          ) : (
            // Cambiar de vista entra con un fundido.
            <div key={vista} className="motion-safe:animate-fade-in">
              {vista === 'tabla' ? (
                <TablaDeClientes filas={visibles} etapas={etapas} ahora={hoy} campos={campos} temperaturas={temperaturas} pref={pref} edicion={edicion} onVerFicha={abrir} chatDe={chatDe} fuentes={nombresDeFuentes} />
              ) : vista === 'kanban' ? (
                <TableroPorEtapa filas={visibles} etapas={etapas} ahora={hoy} onAbrir={abrir} onMover={mover} />
              ) : vista === 'lista' ? (
                <ListaDeClientes filas={visibles} hoy={hoy} orden={orden} elegidoId={elegidoId} onAbrir={abrir} />
              ) : (
                <RejillaDeTarjetas filas={visibles} etapas={etapas} ahora={hoy} onVerFicha={abrir} chatDe={chatDe} />
              )}
            </div>
          )}
        </>
      )}

      {elegido && <VentanaDeFicha key={elegido.contactId} fila={elegido} etapas={etapas} abierta onCerrar={cerrarFicha} />}
    </div>
  );
}
